import { request } from "components/hooks/useQuery";
import { GetStaticPropsContext } from "next";
import { getBlogPosts } from "queries/Blog";
import RelatedCard, { IBlogPost } from "components/base/RelatedCard";
import Container from "components/core/Container";
import Heading from "components/elements/Heading";
import PreHeading from "components/elements/PreHeading";
import React, { useState } from "react";
import Button from "components/elements/Button";
import cn from "classnames";
import { withAppLayout } from "components/core/withAppLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "./../../../next-i18next.config";
import useTrans from "components/hooks/useTrans";
import useRouter from "components/hooks/useRouter";
import { useSpring, animated } from 'react-spring';
import { useCookies } from "react-cookie";

export async function getStaticProps(context: GetStaticPropsContext) {
    const { blog, allBlogPosts, header, footer } = await request({
        query: getBlogPosts(context.locale, 8),
        withAppData: true,
        locale: context.locale
    });

    if (!allBlogPosts) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            data: {
                allBlogPosts
            },
            seo: blog.seo,
            appData: {
                ...header,
                ...footer
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        },
    };
}

function Index(props: any) {
    const { allBlogPosts } = props;
    const { t } = useTrans();
    const [blogPosts, setBlogPosts] = useState(allBlogPosts);
    const { locale, push, pathname, asPath } = useRouter();
    const [cookies, setCookie] = useCookies(['NEXT_LOCALE']);
    const [skip, setSkip] = useState(8);
    const [pending, setPending] = useState(false);
    const imageAppear = useSpring({
        from: { opacity: .5, marginTop: "10rem" },
        to: { opacity: 1, marginTop: "0rem" },
        delay: 50
    });
    const noBlogPosts = blogPosts.length === 0;
    const fetchNextBlogPosts = async () => {
        setPending(true);
        await request({
            query: getBlogPosts(locale, 6, skip)
        }).then((data) => {
            setSkip(skip + 6);
            setBlogPosts((prevState: any) => ([...prevState, ...data.allBlogPosts]));
        })
            .catch((err) => console.log(err))
            .finally(() => {
                setPending(false);
            });
    };

    const changeLanguage = (language: string) => {
        setCookie("NEXT_LOCALE", language);
        push(pathname, asPath, { locale: language });
    };

    const buttonPendingState = cn(
        { "animate-pulse cursor-wait": pending }
    );

    return (
        <Container>
            <div className={"grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:gap-x-8 gap-y-20 pt-10 lg:mt-0 mt-20"}>
                <div className={"w-full h-full bg-primary-25 p-8 relative"}>
                    <Heading renderAs={"h1"}>
                        The <br/>
                        Daily <br/>
                        Piggy <br/>
                    </Heading>

                    <PreHeading
                        size={"small"}
                        className={"tracking-widest mb-3 opacity-50 absolute bottom-4 uppercase"}
                    >
                        {t('ourBlog')}
                    </PreHeading>
                </div>

                {blogPosts.map((blogPost: IBlogPost) => {
                    return (
                        <animated.div style={imageAppear} key={blogPost.id}>
                            <RelatedCard {...blogPost} type={'blog'}/>
                        </animated.div>
                    );
                })}

                {noBlogPosts && (
                    <>
                        <animated.div style={imageAppear} key={'no-blog-posts'}>
                            <div className={"w-full h-full bg-blue-tint p-8 relative"}>
                                <Heading renderAs={"h3"}>
                                    {t('sorryNoBlogsForThisLanguage')} <br/>
                                </Heading>

                                <PreHeading
                                    size={"small"}
                                    className={"tracking-widest mb-3 opacity-50 absolute bottom-4 uppercase"}
                                >
                                    The Daily Piggy
                                    {/*{t('comeBackSoon')}*/}
                                </PreHeading>
                            </div>
                        </animated.div>
                        <animated.div style={imageAppear} key={'no-blog-posts'}>
                            <div className={"w-full h-full bg-blue-tint p-8 relative"}>
                                <div onClick={() => changeLanguage('en')}>
                                    <Heading renderAs={"h4"} className={"hover:text-purple cursor-pointer"}>
                                        {t('readLanguageBlogHere', { language: t('english') })} 🇬🇧 <br/>
                                    </Heading>
                                </div>
                                <div onClick={() => changeLanguage('en')}>
                                    <Heading renderAs={"h4"} className={"hover:text-purple cursor-pointer"}>
                                        {t('readLanguageBlogHere', { language: t('german') })} 🇩🇪 <br/>
                                    </Heading>
                                </div>
                                <div onClick={() => changeLanguage('de')}>
                                    <Heading renderAs={"h4"} className={"hover:text-purple cursor-pointer"}>
                                        {t('readLanguageBlogHere', { language: t('dutch') })} 🇳🇱 <br/>
                                    </Heading>
                                </div>
                                <PreHeading
                                    size={"small"}
                                    className={"tracking-widest mb-3 opacity-50 absolute bottom-4 uppercase"}
                                >
                                    The Daily Piggy
                                    {/*{t('comeBackSoon')}*/}
                                </PreHeading>
                            </div>
                        </animated.div>
                    </>

                )}
            </div>
            <div className={"mx-auto w-full md:w-56 text-center lg:mt-10 lg:mb-20 mt-20"} hidden={noBlogPosts}>
                <Button
                    id={"blog__load-more-articles"}
                    variant={"secondary"}
                    size={"large"}
                    color={"purple"}
                    onClick={fetchNextBlogPosts}
                    className={buttonPendingState}
                >
                    {t('loadMoreArticles')}
                </Button>
            </div>
        </Container>
    );
}

export default withAppLayout(Index);